import { useState } from 'react';
import {
  Users,
  Loader2,
  CheckCircle,
  ChevronDown,
  ChevronRight,
  AlertCircle,
} from 'lucide-react';

interface SubAgentSpawnCardProps {
  args: Record<string, unknown>;
  result?: unknown;
}

interface AgentEntry {
  agentId: string;
  agentName?: string;
  runId?: string;
  status?: string;
  prompt?: string;
}

interface Summary {
  successCount: number;
  failureCount: number;
  totalDurationMs: number;
  tasks?: Array<{ taskId: string; agentId: string; agentName: string; success: boolean; durationMs: number }>;
}

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  const seconds = Math.round(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}m ${secs}s`;
}

export function SubAgentSpawnCard({ args, result }: SubAgentSpawnCardProps) {
  const [expanded, setExpanded] = useState(true);

  const count = (args.count as number) ?? 0;
  const agents = (args.agents as AgentEntry[]) ?? [];

  let summary: Summary | undefined;
  if (result) {
    try {
      summary = typeof result === 'string' ? JSON.parse(result) : (result as Summary);
    } catch { /* ignore */ }
  }

  const isComplete = !!summary;
  const hasFailed = summary && summary.failureCount > 0;

  return (
    <div className="my-2 rounded-md border border-border bg-muted/50 text-sm">
      <button
        type="button"
        className="flex w-full items-center gap-2 px-3 py-2 text-left text-foreground hover:bg-muted/80"
        onClick={() => setExpanded(!expanded)}
      >
        {expanded ? (
          <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
        ) : (
          <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
        )}

        <Users className="h-4 w-4 shrink-0 text-muted-foreground" />

        <span className="font-medium">
          {isComplete
            ? `Sub-agents complete`
            : `Running ${count} sub-agent${count !== 1 ? 's' : ''}`}
        </span>

        <span className="ml-auto flex shrink-0 items-center gap-2">
          {!isComplete && (
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          )}
          {isComplete && !hasFailed && (
            <CheckCircle className="h-4 w-4 text-green-500" />
          )}
          {isComplete && hasFailed && (
            <AlertCircle className="h-4 w-4 text-amber-500" />
          )}
        </span>
      </button>

      {expanded && summary && (
        <div className="border-t border-border px-3 py-2 text-xs text-muted-foreground">
          <span>{summary.successCount}/{summary.successCount + summary.failureCount} succeeded</span>
          {summary.failureCount > 0 && (
            <span className="text-red-500 ml-2">
              {summary.failureCount} failed
            </span>
          )}
          <span className="ml-2">{formatDuration(summary.totalDurationMs)}</span>
        </div>
      )}

      {expanded && !summary && agents.length > 0 && (
        <div className="border-t border-border px-3 py-2 text-xs text-muted-foreground">
          {agents.map((a, i) => (
            <div key={a.runId ?? `pending-${i}`} className="flex items-center gap-1.5 py-0.5">
              {a.status === 'running' && <Loader2 className="h-3 w-3 animate-spin" />}
              {a.status === 'completed' && <CheckCircle className="h-3 w-3 text-green-500" />}
              {a.status === 'failed' && <AlertCircle className="h-3 w-3 text-red-500" />}
              {(!a.status || a.status === 'pending') && <div className="h-3 w-3 rounded-full border border-muted-foreground/40" />}
              <span>{a.agentName || a.agentId}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
